/**
 * useLiveMeetingSurfacing — TASK-667.
 *
 * Unlike every other widget, `LiveMeetingNow` is **pushed**: when a session goes live it
 * appears on the canvas by itself, via `addSpecs` — the same sink the dock uses. It is
 * never a navigation, because the RM is inside a Teams call and must not be pulled out of it.
 *
 * Two correctness requirements the ticket calls out, both handled here rather than in the
 * widget:
 *
 *  - **Discovery must not depend on having received the state-change event.** An RM who
 *    opens the dashboard mid-meeting has missed it. So live sessions are DISCOVERED from
 *    backend state (`listMeetings` filtered to live statuses) on mount and on an interval,
 *    not only from the stream.
 *  - **Dismissal sticks.** Dismissing a session suppresses automatic re-summoning for that
 *    session; otherwise the next poll would immediately put it back, which reads as the app
 *    ignoring the user. Suppression is per-session and lives for the browser session only —
 *    the ended state stays recoverable from `MeetingsList`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { listMeetings, type MeetingListItem } from '../services/api/meetingApi';
import { MEETINGS_QUERY_KEY } from '../services/api/queryKeys';
import { useCanvasActions } from '../stores/canvasStore';

const LIVE_STATUSES = new Set(['live', 'in_progress']);
const POLL_MS = 20_000;

export function isLive(m: Pick<MeetingListItem, 'status'>): boolean {
  return LIVE_STATUSES.has(m.status.toLowerCase());
}

/**
 * TASK-673: only BOT meetings are auto-surfaced as a `LiveMeetingNow` monitor — the RM is
 * inside Teams and has no Kielis surface otherwise. An in-person live meeting must NOT get a
 * pushed bot-monitor tile: it already has its recorder tile from the start flow, and pushing
 * the monitor both duplicates it and offered an "Open meeting" that routed off the canvas.
 */
export function isBotLive(
  m: Pick<MeetingListItem, 'status' | 'meeting_url' | 'capture_source'>,
): boolean {
  return isLive(m) && Boolean(m.meeting_url || m.capture_source === 'attendee-bot');
}

export function useLiveMeetingSurfacing(enabled = true) {
  const { addSpecs } = useCanvasActions();
  // Sessions already summoned in this browser session — the idempotency guard. Without it
  // every poll would append another identical tile.
  const summoned = useRef<Set<string>>(new Set());
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const query = useQuery({
    queryKey: MEETINGS_QUERY_KEY,
    queryFn: listMeetings,
    enabled,
    refetchInterval: POLL_MS,
    staleTime: POLL_MS / 2,
  });

  useEffect(() => {
    // TASK-877 (review): `enabled` has to be checked here, not just relied on through
    // `query.data`.
    //
    // While this hook owned a private cache key, `enabled: false` meant nothing ever
    // populated the entry and `query.data` stayed undefined, so this effect no-opped by
    // accident. Sharing `MEETINGS_QUERY_KEY` with `MeetingsList` ended that: React Query
    // does not blank `data` for a disabled observer, so a mounted `MeetingsList` would
    // fill the cache and this effect would push `LiveMeetingNow` tiles onto the canvas
    // for a caller that explicitly switched surfacing off.
    if (!enabled) return;
    if (!query.data) return;
    // Only bot meetings are pushed; in-person live meetings are driven by the recorder tile.
    const live = query.data.filter(isBotLive);
    const fresh = live.filter(
      (m) => !summoned.current.has(m.session_id) && !dismissed.has(m.session_id),
    );
    if (!fresh.length) return;

    fresh.forEach((m) => summoned.current.add(m.session_id));
    // One tile per session — concurrent live meetings each get their own, addressable.
    addSpecs(
      fresh.map((m) => ({
        component: 'LiveMeetingNow',
        props: {
          sessionId: m.session_id,
          clientName: m.client_name,
          meetingType: m.meeting_type,
        },
      })),
    );
  }, [enabled, query.data, dismissed, addSpecs]);

  const dismiss = useCallback((sessionId: string) => {
    setDismissed((prev) => new Set(prev).add(sessionId));
  }, []);

  return { liveSessions: (query.data ?? []).filter(isLive), dismiss };
}

export default useLiveMeetingSurfacing;
